import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Activity, RefreshCw, ChevronDown, ChevronUp, Wifi, WifiOff, Database, Cog, AlertTriangle } from 'lucide-react'
import { baliseService, type JimiHealth, type JimiComponentStatus } from '../services/baliseService'

// JimiHealthCard — single-row pipeline status card for the JIMI ingest
// chain (TurboHive MQTT → jimi-receiver → jimi-bridge → orchestration_db).
// Designed to be embedded above the inventory statistics row in
// BaliseManagement so the operator notices a stuck pipeline before they
// notice "no events". The card stays a single horizontal row in healthy
// state; clicking the chevron expands a detail panel with per-component
// metrics.
//
// Auto-refresh: every 30s while the card is mounted. Manual refresh
// button is also available. Both paths share the same loader.
//
// Failure-tolerance: if /api/jimi/health itself fails, the card renders a
// muted "unable to fetch health" row instead of crashing the page.

const POLL_INTERVAL_MS = 30_000

function statusColor(status: JimiComponentStatus): { bg: string; border: string; text: string; dot: string } {
  switch (status) {
    case 'OK':
      return { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-700', dot: 'bg-emerald-500' }
    case 'DEGRADED':
    case 'STALE':
      return { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700', dot: 'bg-amber-500' }
    case 'DOWN':
      return { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-700', dot: 'bg-red-500' }
    case 'UNREACHABLE':
    default:
      return { bg: 'bg-gray-50', border: 'border-gray-200', text: 'text-gray-700', dot: 'bg-gray-400' }
  }
}

function formatAge(seconds: number | null): string {
  if (seconds == null) return '—'
  if (seconds < 60) return `${seconds}s`
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ${seconds % 60}s`
  const h = Math.floor(seconds / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  return `${h}h ${m}m`
}

function formatTimestamp(iso: string | null): string {
  if (!iso) return '—'
  try {
    return new Date(iso).toLocaleString()
  } catch {
    return iso
  }
}

const JimiHealthCard: React.FC = () => {
  const { t } = useTranslation()
  const [health, setHealth] = useState<JimiHealth | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [expanded, setExpanded] = useState(false)

  const load = async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await baliseService.getJimiHealth()
      setHealth(data)
    } catch (err: any) {
      setError(err?.message || 'Failed to load JIMI health')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
    const id = window.setInterval(load, POLL_INTERVAL_MS)
    return () => window.clearInterval(id)
  }, [])

  if (error && !health) {
    return (
      <div className="rounded-xl border border-gray-200 bg-gray-50 p-3 flex items-center gap-3 text-sm text-gray-600">
        <AlertTriangle className="w-5 h-5 text-gray-400" />
        <span>{t('jimi_health_load_failed', { defaultValue: 'JIMI pipeline health unavailable' })}: {error}</span>
        <button type="button" onClick={load} className="ml-auto inline-flex items-center gap-1 px-2 py-1 rounded border border-gray-300 hover:bg-white text-xs">
          <RefreshCw className="w-3 h-3" /> {t('retry', { defaultValue: 'Retry' })}
        </button>
      </div>
    )
  }

  if (!health) {
    return (
      <div className="rounded-xl border border-gray-200 bg-gray-50 p-3 flex items-center gap-3 text-sm text-gray-500">
        <Activity className="w-5 h-5 text-gray-400 animate-pulse" />
        <span>{t('jimi_health_loading', { defaultValue: 'Checking JIMI pipeline…' })}</span>
      </div>
    )
  }

  const overall = statusColor(health.overall)
  const orch = statusColor(health.orchestration.status)
  const recv = statusColor(health.receiver.status)
  const brdg = statusColor(health.bridge.status)

  return (
    <div className={`rounded-xl border ${overall.border} ${overall.bg}`}>
      {/* Top row — single line summary */}
      <div className="flex items-center gap-3 p-3">
        <Activity className={`w-5 h-5 ${overall.text} flex-shrink-0`} />
        <div className="flex-1 flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-2">
            <span className={`inline-block w-2.5 h-2.5 rounded-full ${overall.dot}`} />
            <span className={`text-sm font-semibold ${overall.text}`}>
              {t('jimi_health_pipeline', { defaultValue: 'JIMI pipeline' })}: {health.overall}
            </span>
          </div>

          {/* Per-component pills, kept compact so the row stays single-line on desktop */}
          <div className="flex items-center gap-2 text-xs">
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border ${orch.border} ${orch.bg} ${orch.text}`} title={t('jimi_health_orch_tooltip', { defaultValue: 'Last JIMI event in orchestration_db' })}>
              <Database className="w-3 h-3" /> orch · {health.orchestration.status}
              {health.orchestration.secondsSinceLastEvent != null && (
                <span className="opacity-75">· {formatAge(health.orchestration.secondsSinceLastEvent)} ago</span>
              )}
            </span>
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border ${recv.border} ${recv.bg} ${recv.text}`} title={t('jimi_health_recv_tooltip', { defaultValue: 'jimi-receiver MQTT state' })}>
              {health.receiver.mqttConnected ? <Wifi className="w-3 h-3" /> : <WifiOff className="w-3 h-3" />}
              receiver · {health.receiver.status}
              <span className="opacity-75">· {health.receiver.subscribedTopicCount} topics</span>
            </span>
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border ${brdg.border} ${brdg.bg} ${brdg.text}`} title={t('jimi_health_bridge_tooltip', { defaultValue: 'jimi-bridge poll cycle' })}>
              <Cog className="w-3 h-3" /> bridge · {health.bridge.status}
              {health.bridge.watermarkLagSeconds != null && (
                <span className="opacity-75">· lag {formatAge(health.bridge.watermarkLagSeconds)}</span>
              )}
            </span>
          </div>
        </div>

        <button type="button" onClick={load} disabled={loading}
          className="p-1.5 rounded text-gray-500 hover:bg-white/60 disabled:opacity-50"
          title={t('jimi_health_refresh', { defaultValue: 'Refresh now' })}>
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
        </button>
        <button type="button" onClick={() => setExpanded(v => !v)}
          className="p-1.5 rounded text-gray-500 hover:bg-white/60"
          title={expanded ? t('collapse', { defaultValue: 'Collapse' }) : t('expand', { defaultValue: 'Expand' })}>
          {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>
      </div>

      {/* Detail panel — only mounted when expanded so the default render
          stays cheap. Three columns matching the three pipeline stages. */}
      {expanded && (
        <div className="border-t border-gray-200 grid grid-cols-1 md:grid-cols-3 gap-px bg-gray-200">
          <div className="bg-white p-3 text-xs space-y-1">
            <div className="font-semibold text-gray-700 flex items-center gap-1.5">
              <Database className="w-4 h-4 text-blue-600" /> orchestration_db
            </div>
            <div className="text-gray-600">{t('jimi_health_status', { defaultValue: 'Status' })}: <span className={`font-medium ${orch.text}`}>{health.orchestration.status}</span></div>
            <div className="text-gray-600">{t('jimi_health_last_event', { defaultValue: 'Last JIMI event' })}: <span className="font-medium text-gray-900">{formatTimestamp(health.orchestration.lastJimiEventAt)}</span></div>
            <div className="text-gray-600">{t('jimi_health_total_24h', { defaultValue: 'Events (24h)' })}: <span className="font-medium text-gray-900">{health.orchestration.totalJimiEvents24h}</span></div>
            <div className="text-gray-600">{t('jimi_health_devices_24h', { defaultValue: 'Devices seen (24h)' })}: <span className="font-medium text-gray-900">{health.orchestration.jimiDevicesActive24h}</span></div>
            {health.orchestration.error && (
              <div className="text-red-600 break-all">{t('error', { defaultValue: 'Error' })}: {health.orchestration.error}</div>
            )}
          </div>

          <div className="bg-white p-3 text-xs space-y-1">
            <div className="font-semibold text-gray-700 flex items-center gap-1.5">
              {health.receiver.mqttConnected
                ? <Wifi className="w-4 h-4 text-emerald-600" />
                : <WifiOff className="w-4 h-4 text-red-600" />}
              jimi-receiver
            </div>
            <div className="text-gray-600">{t('jimi_health_status', { defaultValue: 'Status' })}: <span className={`font-medium ${recv.text}`}>{health.receiver.status}</span></div>
            <div className="text-gray-600">{t('jimi_health_mqtt', { defaultValue: 'MQTT' })}: <span className="font-medium text-gray-900">{health.receiver.mqttConnected ? 'connected' : 'disconnected'}</span></div>
            <div className="text-gray-600">{t('jimi_health_topics', { defaultValue: 'Subscribed topics' })}: <span className="font-medium text-gray-900">{health.receiver.subscribedTopicCount}</span></div>
            {health.receiver.totals && (
              <div className="text-gray-600">{t('jimi_health_raw_counts', { defaultValue: 'Raw events (all-time)' })}: <span className="font-medium text-gray-900">{(health.receiver.totals as any).all ?? 0}</span></div>
            )}
            {health.receiver.error && (
              <div className="text-red-600 break-all">{t('error', { defaultValue: 'Error' })}: {health.receiver.error}</div>
            )}
          </div>

          <div className="bg-white p-3 text-xs space-y-1">
            <div className="font-semibold text-gray-700 flex items-center gap-1.5">
              <Cog className="w-4 h-4 text-purple-600" /> jimi-bridge
            </div>
            <div className="text-gray-600">{t('jimi_health_status', { defaultValue: 'Status' })}: <span className={`font-medium ${brdg.text}`}>{health.bridge.status}</span></div>
            <div className="text-gray-600">{t('jimi_health_last_cycle', { defaultValue: 'Last cycle' })}: <span className="font-medium text-gray-900">{formatTimestamp(health.bridge.lastCycleAt)}</span></div>
            <div className="text-gray-600">{t('jimi_health_lag', { defaultValue: 'Watermark lag' })}: <span className="font-medium text-gray-900">{formatAge(health.bridge.watermarkLagSeconds)}{health.bridge.staleAfterSeconds != null && <span className="opacity-75"> / {formatAge(health.bridge.staleAfterSeconds)}</span>}</span></div>
            <div className="text-gray-600">{t('jimi_health_cycle_count', { defaultValue: 'Cycles since boot' })}: <span className="font-medium text-gray-900">{health.bridge.cycleCount}</span></div>
            {health.bridge.cumulative && (
              <div className="text-gray-600">{t('jimi_health_cumulative', { defaultValue: 'Cumulative' })}: <span className="font-medium text-gray-900">w {health.bridge.cumulative.written} · d {health.bridge.cumulative.duplicate} · f {health.bridge.cumulative.failed}</span></div>
            )}
            {health.bridge.error && (
              <div className="text-red-600 break-all">{t('error', { defaultValue: 'Error' })}: {health.bridge.error}</div>
            )}
          </div>
        </div>
      )}

      <div className="px-3 pb-2 text-[10px] text-gray-500">
        {t('jimi_health_checked_at', { defaultValue: 'Checked' })}: {formatTimestamp(health.checkedAt)}
      </div>
    </div>
  )
}

export default JimiHealthCard
